import elevateClient from "@/services/elevate/elevate-client";
import { useMarket } from "@features/market/useMarket";
import { Cart } from "@repo/core/cart";
import { useCallback } from "react";
import { Product, Variant } from "types";
import { checkoutDeniedEvent } from "../gtm/checkoutDenied";
import { emailRegistrationEvent } from "../gtm/emailRegistrationEvent";
import { selectItemEvent } from "../gtm/selectItemEvent";
import { TrackedCustomProduct, TrackedPageData } from "../utils";
import { sendVoyadoEmailRegistrationEvent } from "../voyado-tracking";

export default function useTracking() {
  const market = useMarket();

  const legacyTrackProduct = useCallback(
    (product?: Product | Variant) => {
      if (!product) return;

      const trackedProduct: TrackedCustomProduct = {
        ticket: product.ticket,
        item_id: product.id,
        item_name: product.title,
        price: product.price,
        item_brand: product.brand,
        item_category: product?.["CategoryPath.Name"]?.[0],
        item_category2: product?.["CategoryPath.Name"]?.[1],
        item_category3: product?.["CategoryPath.Name"]?.[2],
      };
      void selectItemEvent({
        product: trackedProduct,
        currency: market.currency,
      });
      void elevateClient(market).notify.click(product.ticket);
    },
    [market],
  );

  const trackProduct = useCallback(
    (product: TrackedCustomProduct, pageData: TrackedPageData) => {
      void selectItemEvent({ product, currency: market.currency, pageData });
      void elevateClient(market).notify.click(product.ticket);
    },
    [market],
  );

  const trackTicket = useCallback(
    (ticket: string) => {
      void elevateClient(market).notify.click(ticket);
    },
    [market],
  );

  const checkoutDenied = useCallback(
    (cart?: Cart) => {
      if (!cart) return;
      void checkoutDeniedEvent({
        cart_id: cart.id,
        market_code: market.countryCode,
      });
    },
    [market],
  );

  const emailRegistration = useCallback((email: string) => {
    void emailRegistrationEvent(email);
    void sendVoyadoEmailRegistrationEvent({ email });
  }, []);

  return {
    legacyTrackProduct,
    trackTicket,
    trackProduct,
    checkoutDenied,
    emailRegistration,
  };
}
